import { useOutletContext, useParams } from 'react-router-dom'

import type { CurrentPlayerCtx } from '../../PlayerShell'
import { usePokerView } from './hooks/usePokerView'
import ActiveView from './views/ActiveView'
import EliminatedView from './views/EliminatedView'
import PreDrawView from './views/PreDrawView'
import SeatedView from './views/SeatedView'

/**
 * Bordet dispatcher. Reads phase state via `usePokerView`, then
 * routes to the state-specific view.
 *
 * State machine (see `PokerState` in `hooks/usePokerView.ts`):
 *   no-poker  → "not enabled" message
 *   loading   → loader
 *   error     → error
 *   pre-draw  → PreDrawView (waiting for dealer to draw seats)
 *   seated    → SeatedView (seat reveal hero, clock not yet running)
 *   active    → ActiveView (default — clock + seat + standings)
 *   paused    → ActiveView with paused banner
 *   eliminated→ EliminatedView (variant='eliminated')
 *   winner    → EliminatedView (variant='winner')
 */
const PokerSeat = () => {
  const { player } = useOutletContext<{ player: CurrentPlayerCtx }>()
  const { playerKey = '' } = useParams<{ playerKey: string }>()
  const { view, state, myBB, avgBB, myPosition, payJump, isPaused } =
    usePokerView(player.polfEvent.usePokerPhase)

  if (state === 'no-poker') {
    return (
      <section className="px-6 py-12 text-center">
        <p className="text-ink-500 text-sm">
          Pokerfase er ikke aktivert for denne turneringen.
        </p>
      </section>
    )
  }

  if (state === 'loading') {
    return (
      <section className="px-4 py-8 text-center text-ink-500">
        Laster bordet…
      </section>
    )
  }

  if (state === 'error' || !view) {
    return (
      <section className="px-4 py-8 text-center text-flag">
        Kunne ikke laste pokerstatus.
      </section>
    )
  }

  if (state === 'pre-draw') {
    return <PreDrawView />
  }

  if (state === 'seated' && view.seat) {
    return <SeatedView seat={view.seat} tableSeats={view.tableSeats} />
  }

  if (state === 'eliminated') {
    return (
      <EliminatedView view={view} variant="eliminated" playerKey={playerKey} />
    )
  }

  if (state === 'winner') {
    return <EliminatedView view={view} variant="winner" playerKey={playerKey} />
  }

  // active | paused
  return (
    <ActiveView
      view={view}
      isPaused={isPaused}
      myBB={myBB}
      avgBB={avgBB}
      myPosition={myPosition}
      payJump={payJump}
      playerKey={playerKey}
    />
  )
}

export default PokerSeat
